import { redirect, useNavigate } from "@vixi/router";
import { useForm } from "react-hook-form";
import { useConfirmEmail } from "@/hooks/api/auth/useConfirmEmail";
import { useTranslation } from "@vixi/localization";
import { useEffect, useState } from "react";
import { useResendConfirmationEmail } from "@/hooks/api/auth/useResendConfirmationEmail";
import { RouteOptionsById } from "@vixi/router";
import { RouterIds } from "@vixi/router";
import { Button, FieldLabel, TextInput, StyledTextLink } from "@vixi/ui";
import { getAuthState, useAuthStore } from "@vixi/api";

// Constants
const routeId = "/_auth/confirm-email" satisfies RouterIds;
export const ConfirmEmailPageOptions: RouteOptionsById<typeof routeId> = {
  component: RouteComponent,
  loader: () => {
    if (!getAuthState().registeringUser) {
      throw redirect({
        to: "/login",
      });
    }
  },
};

interface VerifyFormValues {
  code: string;
}

function RouteComponent() {
  const { t, tNum } = useTranslation();
  const navigate = useNavigate();
  const { register, handleSubmit } = useForm<VerifyFormValues>({
    defaultValues: {
      code: "",
    },
  });

  const [resendDelay, setResendDelay] = useState(0);
  useEffect(() => {
    const interval = setInterval(() => {
      if (resendDelay > 0) {
        setResendDelay(resendDelay - 1);
      }
    }, 1000);

    return () => clearInterval(interval);
  }, [resendDelay]);

  const { registeringUser } = useAuthStore();
  const { mutateAsync: confirmEmail, isError } = useConfirmEmail();

  const onSubmit = async ({ code }: VerifyFormValues) => {
    await confirmEmail(code);
    navigate({
      to: "/login",
    });
  };

  const { mutateAsync: resendCode, isPending: resetCodePending } = useResendConfirmationEmail();
  const handleResendCode = async () => {
    setResendDelay(60);
    await resendCode();
  };

  return (
    <>
      <div className="mt-20 flex max-w-90 flex-col gap-y-6">
        <h1 className="font-semibold">{t("Auth.ConfirmEmail.Title")}</h1>

        <div className="flex flex-col gap-y-5" onSubmit={handleSubmit(onSubmit)}>
          <div>
            <FieldLabel size="medium" className="mb-12.5">
              {t("Auth.ConfirmEmail.EnterTheCode")} {registeringUser?.email}{" "}
              {t("Auth.ConfirmEmail.EnterTheCode2")}
            </FieldLabel>

            <FieldLabel size="medium">{t("Auth.ConfirmEmail.Code")}</FieldLabel>
            <TextInput
              type="text"
              autoComplete="off"
              placeholder="Ex: 1234"
              {...register("code")}
            />
          </div>

          {isError && (
            <p className="text-error" color="error">
              {t("ErrorMessage.SomethingWentWrong")}
            </p>
          )}

          <div className="mt-7.5 text-center">
            <Button type="submit" variant="contained" size="medium">
              {t("Action.Continue")}
            </Button>
          </div>
        </div>

        <h4 className="fs-18 mt-5">
          {`${t("Auth.ConfirmEmail.DidntGetCode")} `}
          <StyledTextLink
            underline="none"
            disabled={resetCodePending || resendDelay > 0}
            onClick={handleResendCode}
            className="fs-18 text-foreground"
          >
            {t("Auth.ConfirmEmail.ResendCode")} {resendDelay > 0 ? `(${tNum(resendDelay)})` : ""}
          </StyledTextLink>
        </h4>
      </div>
    </>
  );
}
